import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by sliding to the variant's featured media when it
   * already exists in the gallery, instead of replacing the entire gallery.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const hideVariants = this.dataset.hideVariants === 'true';
    const immediateMediaId = this.#getMediaIdFromVariantPicker(event.target);

    if (immediateMediaId) {
      if (hideVariants) {
        this.#updateVariantMediaVisibility(immediateMediaId);
      }
      this.#selectMediaById(immediateMediaId);
    }

    event.promise
      .then(({ variant, detail }) => {
        if (!detail?.html) return;

        const { html, newProduct, resource } = detail;

        if (newProduct) {
          this.#replaceMediaGallery(html);
          return;
        }

        const mediaId =
          this.#getFeaturedMediaId(resource, variant) ?? immediateMediaId ?? null;

        if (mediaId) {
          if (hideVariants) {
            this.#updateVariantMediaVisibility(mediaId);
          }

          if (this.#selectMediaById(mediaId)) {
            return;
          }
        }

        this.#replaceMediaGallery(html);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Replaces the media gallery with a freshly rendered one from the section response.
   * @param {Document} html
   */
  #replaceMediaGallery(html) {
    const newMediaGallery = html.querySelector('media-gallery');
    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  }

  /**
   * @param {ProductSelectEvent['detail']['resource']} resource
   * @param {Awaited<ProductSelectEvent['promise']>['variant']} variant
   * @returns {string | null}
   */
  #getFeaturedMediaId(resource, variant) {
    const resourceMedia = resource?.featured_media;
    if (resourceMedia && typeof resourceMedia === 'object' && 'id' in resourceMedia && resourceMedia.id != null) {
      return String(resourceMedia.id);
    }

    const variantMedia = variant?.featured_media;
    if (variantMedia && typeof variantMedia === 'object' && 'id' in variantMedia && variantMedia.id != null) {
      return String(variantMedia.id);
    }

    return null;
  }

  /**
   * Reads the featured media id from the variant picker's currently selected option.
   * @param {Element} eventTarget
   * @returns {string | null}
   */
  #getMediaIdFromVariantPicker(eventTarget) {
    const variantPicker =
      eventTarget instanceof HTMLElement && eventTarget.tagName === 'VARIANT-PICKER'
        ? eventTarget
        : eventTarget.closest('variant-picker');

    if (!(variantPicker instanceof HTMLElement)) return null;

    const selectedOption = variantPicker.querySelector('select option[selected], fieldset input:checked');
    if (!(selectedOption instanceof HTMLOptionElement || selectedOption instanceof HTMLInputElement)) {
      return null;
    }

    const mediaId = selectedOption.dataset.optionMediaId;
    return mediaId ? String(mediaId) : null;
  }

  /**
   * Shows the selected variant's media and hides other variant-attached media.
   * @param {string} mediaId
   */
  #updateVariantMediaVisibility(mediaId) {
    const { slideshow } = this.refs;

    if (slideshow?.refs.slides) {
      for (const slide of slideshow.refs.slides) {
        if (!slide.hasAttribute('variant-image')) continue;

        slide.hidden = slide.getAttribute('slide-id') !== mediaId;
      }
    }

    for (const gridItem of this.querySelectorAll('.media-gallery__grid [variant-image]')) {
      if (!(gridItem instanceof HTMLElement)) continue;

      const itemMediaId = gridItem.dataset.mediaId;
      gridItem.hidden = itemMediaId !== mediaId;
    }
  }

  /**
   * Slides or scrolls to an existing media item in the gallery.
   * @param {string} mediaId
   * @returns {boolean} Whether the media was found in the current gallery.
   */
  #selectMediaById(mediaId) {
    const { slideshow } = this.refs;

    if (slideshow?.refs.slides?.length) {
      const matchingSlide = slideshow.refs.slides.find(
        (slide) =>
          slide.getAttribute('slide-id') === mediaId || slide.querySelector(`[data-media-id="${mediaId}"]`)
      );

      if (matchingSlide) {
        const slideId = matchingSlide.getAttribute('slide-id');
        if (slideId) {
          slideshow.select({ id: slideId }, undefined, { animate: true });
        } else {
          const slideIndex = slideshow.slides?.indexOf(matchingSlide);
          if (slideIndex != null && slideIndex >= 0) {
            slideshow.select(slideIndex, undefined, { animate: true });
          }
        }

        return true;
      }
    }

    const gridItem = this.querySelector(`.media-gallery__grid [data-media-id="${mediaId}"]`)?.closest('li');
    if (gridItem) {
      gridItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return true;
    }

    return false;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
